<script lang="ts">
	import classNames from 'classnames';
	import { DropInfoButton } from '../../components/DropInfoButton';
	import { SERIES_GALLERY_LIMIT } from '../../constants/gallery';
	import { EditionsRoutes } from '../../constants/routes';
	import { GetEditionsGalleryDocument } from '../../queries/__generated__/getEditionsGallery.generated';
	import type { EditionsGalleryCardEdition } from '../../utils/getEditionsGalleryCard/getEditionsGalleryCard';
	import { DownloadPressReleaseButton } from './components/DownloadPressReleaseButton';
	import { SeriesGalleryContainer } from './components/SeriesGalleryContainer';
	import { SubseriesItems } from './components/SubseriesItems';
	import type { seriesPageLoad } from './utils/seriesPageLoad/seriesPageLoad';
	import type { seriesPageLoadGql } from './utils/seriesPageLoadGql/seriesPageLoadGql';
	import type { seriesPageServerLoad } from './utils/seriesPageServerLoad/seriesPageServerLoad';
	import type { seriesPageServerLoadGql } from './utils/seriesPageServerLoadGql/seriesPageServerLoadGql';
	import { infiniteApiQuery } from '#lib/api/infiniteApiQuery/infiniteApiQuery.js';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { ArtistLinks } from '#lib/features/artists/components/ArtistLinks/index.js';
	import { getCartIconContext } from '#lib/features/checkout/utils/getCartIconContext/index.js';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/getLiveDropInfoContext.js';
	import { PageBody } from '#lib/features/navigation/components/PageBody/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { gqlClient } from '#lib/gqlClient.js';
	import {
		editionsGalleryItemsQueryKey,
		getEditionsGalleryItems,
	} from '#lib/websites/editions/api/getEditionsGalleryPage.js';
	import { RegisterInterestButtons } from '#lib/websites/register-interest/components/RegisterInterestButtons/index.js';
	import { goto } from '$app/navigation';
	import { navigating, page } from '$app/state';
	import { Breadcrumbs, type Crumb } from '$global/components/Breadcrumbs';
	import { Container } from '$global/components/Container';
	import {
		DropSelect,
		type DropSelectOption,
	} from '$global/components/DropSelect';
	import { Markdown } from '$global/components/Markdown';
	import { Txt } from '$global/components/Txt';
	import { VideoPlayer } from '$global/components/VideoPlayer';
	import { WhiteBorderImageContainer } from '$global/components/WhiteBorderImageContainer';
	import { buildContentKey } from '$global/utils/contentKey';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { getPageData } from '$global/utils/getPageData/getPageData';
	import { getInfiniteData } from '$global/utils/infinite-loading/getInfiniteData';
	import { infiniteQuery } from '$global/utils/infinite-loading/infiniteQuery';
	import type { SeriesPageData } from '$routes/editions/series/[slug]/types';

	const { dropTypes } = getCartIconContext();
	const { liveDropInfoCtx } = getLiveDropInfoContext();
	const dataCy = 'editions-series';
	let expandedDescription = $state(false);

	// While the flag is off the page data and the load-more query are the
	// GraphQL ones; each branch reads the page data through its own shape
	// (rest-migration/feature-flags.md).
	const useRest = FeatureFlags.RestPages['/editions/series/[slug]'];

	let data = $derived(getPageData<SeriesPageData>(page.data));
	let series = $derived(data.series);
	let dropPageSlug = $derived(data.dropPageSlug);
	let drops = $derived(data.drops ?? []);
	let hasMultipleDrops = $derived(drops.length > 1);

	// The shared DropSelect (also used by the wishlist drawer) renders one option
	// per drop when a series spans more than one, labelled with the drop page name.
	let dropOptions: DropSelectOption[] = $derived(
		drops.map((drop) => ({ value: drop.slug, label: drop.name }))
	);

	const goToDrop = (option: DropSelectOption) => {
		goto(`${GlobalRoutes.EditionsHome}/${option.value}`);
	};
	// While a client navigation to a different series is in flight (parent →
	// subseries), SvelteKit keeps this page on screen until the new page's
	// data arrives — on a slow load (cold ISR) the hero then poses as the next
	// series' artwork for seconds. Hiding the artwork while the grey ground
	// stays makes the wait read as loading rather than as the wrong image.
	let navigatingToOtherSeries = $derived(
		navigating.to?.route?.id === page.route.id &&
			navigating.to?.params?.slug !== page.params.slug
	);

	let parentSeries = $derived(series?.parent_series?.[0]?.Series_id);
	let titleName = $derived(parentSeries ? parentSeries?.name : series?.name);
	let videoUrl = $derived(series?.video_url || '');
	let artists = $derived(series?.artists);

	// Partial content key (HB-959): a series page covers many editions, so there
	// is no single artwork id or price to send — the series id stands in as the
	// entity, and buildContentKey drops the fields this surface cannot answer.
	let contentKey = $derived(
		buildContentKey({
			artist: artists
				?.map((artist) => artist?.Artists_id?.name)
				.filter(Boolean)
				.join(', '),
			idValue: series?.id == null ? undefined : String(series.id),
			idCollection: 'Series',
			release: series?.name,
			category: series?.name,
			platform: 'editions',
		})
	);
	let descriptionToShow = $derived(
		expandedDescription ? series?.long_description : series?.short_description
	);
	let descriptionTokensToShow = $derived(
		expandedDescription
			? data.longDescriptionTokens
			: data.shortDescriptionTokens
	);

	let getCrumbs = $derived(() => {
		const items: Crumb[] = [
			{ label: 'Home', href: GlobalRoutes.Home },
			{ label: 'Editions', href: GlobalRoutes.EditionsHome },
			{ label: 'Artists', href: EditionsRoutes.Artists },
			{
				label: '',
				crumbs: (artists || []).map((artist) => ({
					label: `${artist?.Artists_id?.name}`,
					href: `${GlobalRoutes.ArtistsHome}/${artist?.Artists_id?.slug}`,
				})),
			},
		];

		if (parentSeries) {
			items.push({
				label: `${parentSeries?.name}`,
				href: `${EditionsRoutes.Series}/${parentSeries?.slug}`,
			});
		}

		items.push({ label: `${series?.name}` });

		return items;
	});

	let crumbs = $derived(getCrumbs());
	let lastEditionCode = $derived(data.lastEditionCode);

	// The series' drop counts as active while its last edition is still live —
	// the same signal the register-interest block below keys off.
	let isDropActive = $derived(
		!!liveDropInfoCtx.liveDropInfo?.liveEditionsCodes.includes(lastEditionCode)
	);
	let pressReleaseHref = $derived(
		(!isDropActive && series?.inactive_press_release_pdf_link) ||
			series?.press_release_pdf_link ||
			''
	);
	// The route's PageData is a union of the two server loads while the flag is
	// in place, so each branch reads its own shape off it.
	let restData = $derived(
		data as unknown as Awaited<ReturnType<typeof seriesPageLoad>> &
			Awaited<ReturnType<typeof seriesPageServerLoad>>
	);
	let gqlData = $derived(
		data as unknown as Awaited<ReturnType<typeof seriesPageLoadGql>> &
			Awaited<ReturnType<typeof seriesPageServerLoadGql>>
	);

	let maxItems = $derived(
		useRest
			? restData.editions.total || 0
			: gqlData.editions.Editions_aggregated[0]?.count?.name || 0
	);

	let restQuery = $derived(
		useRest
			? infiniteApiQuery({
					queryKey: editionsGalleryItemsQueryKey(restData.params),
					queryFn: (offset) =>
						getEditionsGalleryItems(undefined, { ...restData.params, offset }),
					limit: SERIES_GALLERY_LIMIT,
					maxItems,
				})
			: undefined
	);

	let gqlQuery = $derived(
		useRest
			? undefined
			: infiniteQuery({
					gqlClient,
					variables: gqlData.editionsVariables,
					document: GetEditionsGalleryDocument,
					limit: SERIES_GALLERY_LIMIT,
					maxItems,
				})
	);

	interface GridRow {
		artists?:
			({ Artists_id?: { name?: string | null } | null } | null)[] | null;
		image?: {
			filename_disk?: string | null;
			width?: number | null;
			height?: number | null;
		} | null;
	}

	const toCardEdition = <T extends GridRow>(item: T) => ({
		...item,
		image: {
			url: getImageUrl(item?.image?.filename_disk, '512px'),
			width: item?.image?.width || 0,
			height: item?.image?.height || 0,
		},
		artists: (item?.artists || [])
			.map((artist) => artist?.Artists_id?.name || '')
			.join(', '),
	});

	// The REST row differs from the GraphQL one only in its integer ids, which
	// the card never reads.
	let editions = $derived(
		useRest
			? (getInfiniteData({
					query: restQuery!,
					transform: (page) => page.editions.map(toCardEdition),
				}) as unknown as EditionsGalleryCardEdition[] | undefined)
			: (getInfiniteData({
					query: gqlQuery!,
					transform: (page) => page.Editions.map(toCardEdition),
				}) as unknown as EditionsGalleryCardEdition[] | undefined)
	);

	let query = $derived(useRest ? restQuery! : gqlQuery!);

	const handleReadMoreClick = () => {
		expandedDescription = !expandedDescription;
	};
</script>

{#snippet dropInfoControl(fullWidth: boolean)}
	{#if hasMultipleDrops}
		<DropSelect
			{dataCy}
			label="Drop info"
			variant="secondary"
			direction="up"
			floating
			options={dropOptions}
			onSelect={goToDrop}
			{fullWidth}
		/>
	{:else if dropPageSlug}
		<DropInfoButton {fullWidth} {dataCy} slug={dropPageSlug} />
	{/if}
{/snippet}

<PageBody>
	<Breadcrumbs {dataCy} {crumbs} />

	<Container {dataCy} class="mt-10 md:mt-16">
		<div class="mb-10 lg:hidden">
			{#if videoUrl}
				<VideoPlayer
					class="pb-[56.25%]"
					{dataCy}
					src={videoUrl}
					title={series?.name || ''}
				/>
			{/if}

			{#if !videoUrl && series?.image}
				<!-- Keyed by slug: navigating parent → subseries stays on the same
				     route, so this component (and its img element) is reused, and a
				     reused img keeps showing the previous series' image until the
				     new file has downloaded. Recreating it shows the neutral ground
				     instead of the wrong artwork. -->
				{#key series.slug}
					<WhiteBorderImageContainer
						class="h-[340px]"
						classes={{
							image: navigatingToOtherSeries ? 'opacity-0' : undefined,
						}}
						{dataCy}
						image={{
							url: getImageUrl(series?.image.filename_disk, '2048px') || '',
							width: series?.image.width || 0,
							height: series?.image.height || 0,
						}}
						alt={series?.name || ''}
					/>
				{/key}
			{/if}
		</div>

		<div>
			<Txt variant="h1" class="mb-4 flex flex-wrap justify-between gap-3">
				<span data-cy={`${dataCy}-title`}>{titleName}</span>
				<span data-cy={`${dataCy}-code`} class="whitespace-nowrap"
					>{series?.code}</span
				>
			</Txt>

			<ArtistLinks
				artists={artists ?? []}
				artistTxtVariant="h4"
				artistClass="font-medium"
				artistDataCy={`${dataCy}-artist`}
			/>
		</div>

		<hr class="my-10 border-t border-t-gray-200" />

		<div class="mb-10 grid gap-6 lg:grid-cols-2">
			<div>
				{#if descriptionToShow}
					<Markdown
						dataCy={`${dataCy}-description`}
						class="mb-2.5"
						source={descriptionTokensToShow ?? []}
					/>
				{/if}

				{#if series?.long_description !== series?.short_description}
					<button onclick={handleReadMoreClick}
						><Txt class="underline"
							>{expandedDescription ? 'Read less' : 'Read more'}</Txt
						></button
					>
				{/if}

				{#if pressReleaseHref && dropPageSlug}
					<div class="mt-6 grid grid-cols-2 gap-4 lg:hidden">
						<DownloadPressReleaseButton {dataCy} href={pressReleaseHref} />
						{@render dropInfoControl(true)}
					</div>
				{:else if dropPageSlug}
					<div class="mt-6 lg:hidden">
						{@render dropInfoControl(false)}
					</div>
				{:else if pressReleaseHref}
					<div class="mt-6 lg:hidden">
						<DownloadPressReleaseButton {dataCy} href={pressReleaseHref} />
					</div>
				{/if}

				{#if series?.code && series?.show_register_interest && !!$dropTypes && !liveDropInfoCtx.liveDropInfo?.liveEditionsCodes.includes(lastEditionCode)}
					<div class="mt-6 mb-10">
						<RegisterInterestButtons
							{dataCy}
							code={series?.code}
							{contentKey}
						/>
					</div>
				{/if}
			</div>

			<div class="hidden lg:block">
				{#if videoUrl}
					<VideoPlayer
						controls={false}
						class="pb-[56.25%]"
						{dataCy}
						src={videoUrl}
						title={series?.name || ''}
					/>
				{/if}

				{#if !videoUrl && series?.image}
					<!-- Keyed for the same reason as the mobile image above. -->
					{#key series.slug}
						<WhiteBorderImageContainer
							class="h-[340px]"
							classes={{
								image: navigatingToOtherSeries ? 'opacity-0' : undefined,
							}}
							{dataCy}
							image={{
								url: getImageUrl(series?.image.filename_disk, '2048px') || '',
								width: series?.image.width || 0,
								height: series?.image.height || 0,
							}}
							alt={series?.name || ''}
						/>
					{/key}
				{/if}

				<div
					class={classNames(
						'mt-4 hidden gap-4 lg:grid',
						pressReleaseHref && dropPageSlug ? 'grid-cols-2' : 'grid-cols-1'
					)}
				>
					{#if pressReleaseHref}
						<DownloadPressReleaseButton {dataCy} href={pressReleaseHref} />
					{/if}

					{#if dropPageSlug}
						{@render dropInfoControl(true)}
					{/if}
				</div>
			</div>
		</div>

		<SubseriesItems />
	</Container>

	<SeriesGalleryContainer {query} {editions} count={maxItems} />
</PageBody>
